"use client"
import { TopMainBannerModelDto } from "api"
import { Font } from "components/shopFeatures/wizard/constants"
import { useEffect, useState } from "react"
import styled, { css } from "styled-components"
import { HomePageContainer, StyledSvgIcon } from "theme/shared"

// Dismiss state lives for the whole browser session (cleared when the tab closes)
const SESSION_KEY = "mazda-announcement-dismissed"

// AC breakpoint: mobile is <=820px (between the theme's sm=600 and md=960)
const DESKTOP = "@media (min-width: 821px)"

const StyledBar = styled.aside`
  width: 100%;
  /* Mobile: site paper tone. Desktop: white strip. */
  background-color: ${(p) => p.theme.colors.neutral200};
  ${DESKTOP} {
    background-color: ${(p) => p.theme.colors.white};
  }
`

const StyledContainer = styled(HomePageContainer)`
  padding-top: 12px;
  padding-bottom: 14px;
  ${DESKTOP} {
    padding-top: 16px;
    padding-bottom: 18px;
  }
`

const Dot = styled.span`
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${(p) => p.theme.colors.newPrimaryRed};
`

// label / text arrive as raw CKEditor HTML — collapse the <p> wrapper and
// keep any inline <a> link looking like part of the copy.
const richText = css`
  font-family: ${Font.OENaPro};
  line-height: 18px;
  color: ${(p) => p.theme.colors.darkBlack};

  p {
    margin: 0;
  }
  a {
    color: inherit;
    text-decoration: underline;
  }
  ${DESKTOP} {
    letter-spacing: 0.8px;
  }
`

const Label = styled.span`
  ${richText};
  flex-shrink: 0;
  font-weight: 700;
  font-size: 16px;
  p {
    display: inline;
  }
`

const Text = styled.span`
  ${richText};
  font-weight: 400;
  font-size: 14px;
  text-align: right;
  ${DESKTOP} {
    font-size: 16px;
    p {
      display: inline;
    }
  }
`

const CloseButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  background: none;
  border: none;
  line-height: 0;
  cursor: pointer;
  color: ${(p) => p.theme.colors.darkBlack};
`

// ---- Desktop: single centered row, close button pinned to the inline-end ----
const DesktopRow = styled.div`
  display: none;
  ${DESKTOP} {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
`

const Side = styled.div`
  flex: 1 1 0;
  display: flex;
  align-items: center;
`

const SideEnd = styled(Side)`
  justify-content: flex-start;
`

const DesktopGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

// ---- Mobile: label row + close on first row, promo text wraps below ----
const MobileCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  ${DESKTOP} {
    display: none;
  }
`

const MobileTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const MobileLabelGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

type Props = {
  data: TopMainBannerModelDto
}

const TopBanner = ({ data }: Props) => {
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    try {
      if (sessionStorage.getItem(SESSION_KEY)) setDismissed(true)
    } catch {
      // sessionStorage unavailable (private mode / SSR) — keep the bar visible
    }
  }, [])

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(SESSION_KEY, "1")
    } catch {
      // ignore write failures, just hide for this render
    }
    setDismissed(true)
  }

  // topBanner is null when the CMS toggle is off — render nothing, no whitespace
  if (!data || dismissed) return null

  const { label, text } = data
  if (!label && !text) return null

  return (
    <StyledBar>
      <StyledContainer>
        <DesktopRow>
          <SideEnd>
            <CloseButton
              type="button"
              aria-label="סגירת הודעה"
              onClick={handleDismiss}
            >
              <StyledSvgIcon
                src="/icons/close.svg"
                alt="סגירה"
                $w={14}
                $h={14}
                style={{
                  objectFit: "contain",
                }}
              />
            </CloseButton>
          </SideEnd>
          <DesktopGroup>
            <Dot />
            <Label dangerouslySetInnerHTML={{ __html: label }} />
            <Text dangerouslySetInnerHTML={{ __html: text }} />
          </DesktopGroup>
          <Side />
        </DesktopRow>

        <MobileCol>
          <MobileTopRow>
            <MobileLabelGroup>
              <Dot />
              <Label dangerouslySetInnerHTML={{ __html: label }} />
            </MobileLabelGroup>
            <CloseButton
              type="button"
              aria-label="סגירת הודעה"
              onClick={handleDismiss}
            >
              <StyledSvgIcon
                src="/icons/close.svg"
                alt="סגירה"
                $w={14}
                $h={14}
                style={{
                  objectFit: "contain",
                }}
              />
            </CloseButton>
          </MobileTopRow>
          <Text dangerouslySetInnerHTML={{ __html: text }} />
        </MobileCol>
      </StyledContainer>
    </StyledBar>
  )
}

export default TopBanner
